import React, { CSSProperties } from 'react'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import MUIDialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button, { ButtonProps } from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import { X } from 'lib/lucide'

import { useStyles } from './styles'

interface DialogProps {
  isOpen: boolean
  title: string
  subtitle?: string
  titleAccessory?: React.ReactNode
  actionsAccessory?: React.ReactNode
  contentStyle?: CSSProperties
  style?: CSSProperties
  className?: string
  contentClassName?: string
  showActions?: boolean
  okButtonText?: string
  okButtonProps?: ButtonProps
  cancelButtonText?: string
  onAccept?: () => void
  onCancel: () => void
  isLoading?: boolean
  overflow?: boolean
  readOnly?: boolean
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onCancel,
  title,
  subtitle,
  titleAccessory,
  actionsAccessory,
  children,
  showActions = false,
  okButtonText,
  okButtonProps,
  cancelButtonText,
  onAccept = () => {},
  isLoading,
  contentStyle,
  style,
  className,
  contentClassName,
  overflow,
  readOnly,
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const paperClassName = overflow ? classes.overflow : ''

  return (
    <MUIDialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onCancel}
      closeAfterTransition
      PaperProps={{ className: `${paperClassName} ${className}`, style }}
    >
      <DialogTitle>
        <div className={classes.titleRow}>
          <Typography variant="h6" component="div" className={classes.title}>
            {title}
          </Typography>
          <div className={classes.titleAccessory}>
            {titleAccessory}
            <IconButton className={classes.closeButton} onClick={onCancel} aria-label="Cerrar">
              <X size={20} />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent
        dividers
        style={contentStyle}
        className={`${classes.dialogContent} ${contentClassName ?? ''}`}
      >
        <>
          {subtitle && ( // Solo se muestra si `subtitle` está definido
            <Typography variant="subtitle1" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
          {children}
          {isLoading && (
            <Box mt={4}>
              <LinearProgress />
            </Box>
          )}
        </>
      </DialogContent>

      {showActions && (
        <DialogActions disableSpacing className={classes.actions}>
          {actionsAccessory && <div className={classes.actionsStart}>{actionsAccessory}</div>}
          <div className={classes.actionsGroup}>
            <Button variant="outlined" className={classes.actionButton} onClick={onCancel}>
              {cancelButtonText ?? (readOnly ? 'Cerrar' : 'Cancelar')}
            </Button>
            {!readOnly && (
              <Button
                onClick={onAccept}
                variant="contained"
                color="primary"
                autoFocus
                {...okButtonProps}
                className={`${classes.actionButton} ${okButtonProps?.className ?? ''}`}
              >
                {okButtonText ?? 'Aceptar'}
              </Button>
            )}
          </div>
        </DialogActions>
      )}
    </MUIDialog>
  )
}

export { Dialog }
